import { useMutation, useQuery } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'

import {
  CheckFormControlConditionsRequest,
  ConditionType,
  FormUpdateResponse,
} from '@tabeeb/contracts/main-api/swagger-main-api'

import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { useEffect, useState } from 'react'
import { uniqBy } from 'lodash'
import { TenantFormsQueryKeys } from '../queries'

type SncForm = FormUpdateResponse & {
  AttachedByForm?: { formId: number; controlId: number }
}

const getDependentForms = (
  rootFormId: number,
  controlId: number,
  openedForms: SncForm[],
  ignoreControls: boolean = false
): SncForm[] => {
  const formsForRemove: SncForm[] = []

  for (const dependentForm of openedForms.filter(
    (f) => f.AttachedByForm?.formId === rootFormId && (ignoreControls || f.AttachedByForm.controlId === controlId)
  )) {
    formsForRemove.push(dependentForm)

    const childDependentForms = getDependentForms(dependentForm.Id, 0, openedForms, true)
    if (childDependentForms.length > 0) {
      formsForRemove.push(...childDependentForms)
    }
  }

  return uniqBy(formsForRemove, (f) => f.Id)
}

const useSnc = ({ enabled = true, subdomain }: { enabled?: boolean; subdomain: string }) => {
  const dispatch = useDispatch()

  const { mainApi: api } = useApi()

  const query = useQuery({
    queryFn: () => api.tenantForm.getTenantFormBySubdomainAsync(subdomain),
    queryKey: [TenantFormsQueryKeys.TenantSncForm, subdomain],
    enabled,
  })

  const [activeForms, setActiveForms] = useState<SncForm[]>([])

  useEffect(() => {
    if (query.data) {
      setActiveForms((prev) => [query.data])
    }
  }, [query.data])

  const checkConditionsMutation = useMutation({
    mutationFn: async (request: CheckFormControlConditionsRequest & { FormId: number }) => {
      const dependentForms = getDependentForms(request.FormId, request.FormControlId, activeForms)
      if (dependentForms.length > 0) {
        setActiveForms((prev) => prev.filter((form) => !dependentForms.some((f) => f.Id === form.Id)))
      }

      const condition = await api.tenantForm.checkConditionsAsync(request)
      if (!condition) {
        return condition
      }

      if (condition.Type === ConditionType.AttachForm) {
        const additionalForm = await api.tenantForm.getAdditionalTenantFormAsync(parseInt(condition.Solution!, 10))

        setActiveForms((prev) => [
          ...prev,
          {
            ...additionalForm,
            AttachedByForm: {
              formId: request.FormId,
              controlId: request.FormControlId,
            },
          },
        ])
      }

      return condition
    },
    onSuccess: (data) => {},
    onError: (error) => {
      dispatch(onAddErrorNotification({ message: 'Failed to process answer' }))
    },
  })

  return {
    fetching: query.isFetching,
    loading: query.isLoading,
    processing: checkConditionsMutation.isLoading,
    onProcessAnswer: checkConditionsMutation.mutate,
    forms: activeForms,
    onReload: query.refetch,
    onReset: () => setActiveForms(query.data ? [query.data] : []),
  }
}

export default useSnc
